import { useOnboarding } from '../../../contexts/OnboardingContext'
import { Shield, MapPin, QrCode, Camera, Clock, Wifi } from 'lucide-react'

export function ConstraintsStep() {
  const { data, updateData, nextStep, previousStep } = useOnboarding()
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    nextStep()
  }
  
  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm p-8">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Configure clock-in requirements
        </h2>
        <p className="text-gray-600">
          Set up how employees will clock in and out to ensure accurate time tracking
        </p>
      </div>
      
      <div className="space-y-6">
        {/* Clock-in methods */}
        <div>
          <h3 className="flex items-center text-lg font-medium text-gray-900 mb-4">
            <Shield className="w-5 h-5 mr-2" />
            Required Verification Methods
          </h3>
          <div className="space-y-4">
            {/* Geofence requirement */}
            <div className="p-4 border border-gray-200 rounded-lg">
              <label className="flex items-start">
                <input
                  type="checkbox"
                  checked={data.requireGeo || false}
                  onChange={(e) => updateData({ requireGeo: e.target.checked })}
                  className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mt-0.5"
                />
                <div className="ml-3">
                  <div className="flex items-center">
                    <MapPin className="w-4 h-4 mr-2 text-blue-600" />
                    <span className="font-medium text-gray-900">Location Verification</span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    Employees must be within the geofenced area to clock in/out
                  </p>
                </div>
              </label>
            </div>
            
            {/* QR Code requirement */}
            <div className="p-4 border border-gray-200 rounded-lg">
              <label className="flex items-start">
                <input
                  type="checkbox"
                  checked={data.requireQR || false}
                  onChange={(e) => updateData({ requireQR: e.target.checked })}
                  className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mt-0.5"
                />
                <div className="ml-3">
                  <div className="flex items-center">
                    <QrCode className="w-4 h-4 mr-2 text-blue-600" />
                    <span className="font-medium text-gray-900">QR Code Scanning</span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    Employees must scan a QR code at the work site
                  </p>
                </div>
              </label>
            </div>
            
            {/* Photo requirement */}
            <div className="p-4 border border-gray-200 rounded-lg">
              <label className="flex items-start">
                <input
                  type="checkbox"
                  checked={data.requirePhoto || false}
                  onChange={(e) => updateData({ requirePhoto: e.target.checked })}
                  className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mt-0.5"
                />
                <div className="ml-3">
                  <div className="flex items-center">
                    <Camera className="w-4 h-4 mr-2 text-blue-600" />
                    <span className="font-medium text-gray-900">Photo Verification</span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    Employees must take a photo when clocking in/out
                  </p>
                </div>
              </label>
            </div>
            
            {/* WiFi requirement */}
            <div className="p-4 border border-gray-200 rounded-lg">
              <label className="flex items-start opacity-50 cursor-not-allowed">
                <input
                  type="checkbox"
                  checked={false}
                  disabled
                  className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mt-0.5"
                />
                <div className="ml-3">
                  <div className="flex items-center">
                    <Wifi className="w-4 h-4 mr-2 text-gray-400" />
                    <span className="font-medium text-gray-500">WiFi Network Verification</span>
                    <span className="ml-2 px-2 py-0.5 text-xs bg-gray-200 text-gray-600 rounded-full">Coming soon</span>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    Employees must be connected to an authorized WiFi network
                  </p>
                </div>
              </label>
            </div>
          </div>
        </div>
        
        {/* Time windows */}
        <div>
          <h3 className="flex items-center text-lg font-medium text-gray-900 mb-4">
            <Clock className="w-5 h-5 mr-2" />
            Allowed Clock-In Times (Optional)
          </h3>
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600 mb-4">
              Restrict when employees can clock in and out. Leave empty to allow any time.
            </p>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Earliest Clock-In Time
                </label>
                <input
                  type="time"
                  onChange={(e) => {
                    const timeWindows = data.timeWindows || []
                    const updatedWindows = [...timeWindows]
                    if (!updatedWindows[0]) {
                      updatedWindows[0] = {
                        daysOfWeek: [1, 2, 3, 4, 5], // Mon-Fri
                        startTime: e.target.value,
                        endTime: '17:00'
                      }
                    } else {
                      updatedWindows[0].startTime = e.target.value
                    }
                    updateData({ timeWindows: updatedWindows })
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Latest Clock-Out Time
                </label>
                <input
                  type="time"
                  onChange={(e) => {
                    const timeWindows = data.timeWindows || []
                    const updatedWindows = [...timeWindows]
                    if (!updatedWindows[0]) {
                      updatedWindows[0] = {
                        daysOfWeek: [1, 2, 3, 4, 5], // Mon-Fri
                        startTime: '09:00',
                        endTime: e.target.value
                      }
                    } else {
                      updatedWindows[0].endTime = e.target.value
                    }
                    updateData({ timeWindows: updatedWindows })
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>
        </div>
        
        {/* Summary */}
        {!data.requireGeo && !data.requireQR && !data.requirePhoto && (
          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-800">
              <strong>No verification methods selected.</strong> Employees will be able to clock in/out freely without location or identity verification.
            </p>
          </div>
        )}
      </div>
      
      {/* Navigation */}
      <div className="mt-8 flex justify-between">
        <button
          type="button"
          onClick={previousStep}
          className="px-6 py-2 text-gray-600 hover:text-gray-900 font-medium"
        >
          Back
        </button>
        <button
          type="submit"
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
        >
          Continue
        </button>
      </div>
    </form>
  )
}

export default ConstraintsStep